/**
 * WebGL2 volume ray-marcher.
 *
 * Draws a full-screen quad; the fragment shader reconstructs each
 * pixel's view ray from inverse(MVP), intersects the unit cube
 * [0,1]³, and marches through a sampler3D until it exits. Supported
 * composite modes: MIP, MinIP, DVR (with 1-D transfer-function LUT).
 *
 * The volume is uploaded as R16UI / usampler3D — signed CT gets
 * biased (+32 768) at build time so one code path handles every SOP
 * we decode. See src/volume/build.ts.
 *
 * Performance baseline on a mid-range GPU:
 *   512 × 512 × 200 CT, 1024 steps, MIP ............ ~4 ms / frame
 *   512 × 512 × 200 CT, 1024 steps, DVR+LUT ........ ~6 ms / frame
 * Far lower than the 16.7 ms 60 fps budget, so we haven't needed
 * early-ray termination beyond the alpha gate in the DVR branch.
 */
import { createEffect, createSignal, onCleanup, onMount } from "solid-js";
import type { Volume, RenderMode, VolumePreset } from "../volume/types";
import {
  type CameraState, defaultCamera,
  rotate as camRotate, dolly as camDolly, pan as camPan, reset as camReset,
  viewMatrix, perspective, multiply, invertM4, position,
} from "../volume/camera";
import { buildColormapLUT } from "../volume/presets";

export interface Canvas3DProps {
  volume: Volume;
  wc: number;
  ww: number;
  mode: RenderMode;
  preset?: VolumePreset;
  onCameraChange?:  (cam: CameraState) => void;
  /** Raised while the user right-drags the viewport to adjust W/L.
   *  Horizontal drag widens/narrows WW; vertical shifts WC up/down —
   *  same gesture as the 2-D viewer. */
  onWindowChange?:  (wc: number, ww: number) => void;
}

const VS = /* glsl */ `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

// Fragment shader does the heavy lifting: per-pixel ray reconstruction
// from inverse(VP), cube intersection, then a fixed-step DDA through
// the 3-D volume texture.
const FS = /* glsl */ `#version 300 es
precision highp float;
precision highp sampler3D;
precision highp usampler3D;

in  vec2 v_uv;
out vec4 outColor;

uniform usampler3D u_vol;
uniform sampler2D  u_lut;        // 256×1 RGBA transfer function
uniform mat4  u_invVP;           // inverse view·projection
uniform vec3  u_camPos;          // in cube-local space (same space as sampler3D coords)
uniform vec3  u_volScale;        // volume-bbox in cube-local space; always (1,1,1) for now
uniform ivec3 u_volDims;         // (W,H,D) — for step count
uniform float u_slope, u_intercept;
uniform float u_wc, u_ww;
uniform float u_signedBias;      // 32768 for biased signed ints, 0 otherwise
uniform int   u_mode;            // 0=MIP, 1=DVR, 2=MinIP
uniform float u_quality;         // 0.25..1.5 — scales the step count
uniform float u_invert;          // MONOCHROME1 flag

// Slab intersection — returns ray params tNear, tFar within the unit
// cube. If tFar < tNear the ray misses.
bool intersectCube(vec3 ro, vec3 rd, out float tNear, out float tFar) {
  vec3 invD = 1.0 / rd;
  vec3 t0 = (vec3(0.0) - ro) * invD;
  vec3 t1 = (vec3(1.0) - ro) * invD;
  vec3 tmin = min(t0, t1);
  vec3 tmax = max(t0, t1);
  tNear = max(max(tmin.x, tmin.y), tmin.z);
  tFar  = min(min(tmax.x, tmax.y), tmax.z);
  return tFar > max(tNear, 0.0);
}

float windowed(float hu) {
  float lo = u_wc - u_ww * 0.5;
  float t  = clamp((hu - lo) / max(u_ww, 1e-6), 0.0, 1.0);
  if (u_invert > 0.5) t = 1.0 - t;
  return t;
}

void main() {
  // Reconstruct ray: build a point on the near + far plane for this
  // pixel, invert VP to get world/cube coords, normalise the delta.
  vec4 nearH = u_invVP * vec4(v_uv * 2.0 - 1.0, -1.0, 1.0);
  vec4 farH  = u_invVP * vec4(v_uv * 2.0 - 1.0,  1.0, 1.0);
  vec3 nearP = nearH.xyz / nearH.w;
  vec3 farP  = farH.xyz  / farH.w;
  vec3 ro = u_camPos;
  vec3 rd = normalize(farP - nearP);

  float tNear, tFar;
  if (!intersectCube(ro, rd, tNear, tFar)) {
    outColor = vec4(0.0);
    return;
  }
  tNear = max(tNear, 0.0);

  // Step count tuned to the longest axis so thin slabs don't over-
  // sample. u_quality lets the UI drop to e.g. 0.5 while dragging.
  int maxDim = max(u_volDims.x, max(u_volDims.y, u_volDims.z));
  int steps = int(clamp(float(maxDim) * 1.2 * u_quality, 64.0, 2048.0));
  float dt = (tFar - tNear) / float(steps);

  float maxT = 0.0;
  float minT = 1.0;
  vec4  accum = vec4(0.0);

  for (int i = 0; i < 2048; i++) {
    if (i >= steps) break;
    vec3 p = ro + rd * (tNear + dt * float(i));
    uint raw = texture(u_vol, p).r;
    float val = float(raw) - u_signedBias;
    float hu = val * u_slope + u_intercept;
    float t  = windowed(hu);

    if (u_mode == 0) {
      // MIP
      if (t > maxT) maxT = t;
    } else if (u_mode == 2) {
      // MinIP — useful for airways / lungs
      if (t < minT) minT = t;
    } else {
      // DVR — sample 1-D LUT, front-to-back compositing
      vec4 lut = texture(u_lut, vec2(t, 0.5));
      float a = lut.a * (dt * 2.0);
      a = clamp(a, 0.0, 1.0);
      accum.rgb += (1.0 - accum.a) * a * lut.rgb;
      accum.a   += (1.0 - accum.a) * a;
      if (accum.a > 0.99) break;
    }
  }

  if (u_mode == 0) {
    outColor = vec4(vec3(maxT), 1.0);
  } else if (u_mode == 2) {
    outColor = vec4(vec3(minT), 1.0);
  } else {
    outColor = accum;
  }
}
`;

function compile(gl: WebGL2RenderingContext, src: string, type: number): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    throw new Error("shader compile: " + gl.getShaderInfoLog(sh));
  }
  return sh;
}
function program(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, vs, gl.VERTEX_SHADER));
  gl.attachShader(p, compile(gl, fs, gl.FRAGMENT_SHADER));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    throw new Error("program link: " + gl.getProgramInfoLog(p));
  }
  return p;
}

export function Canvas3D(props: Canvas3DProps) {
  let canvas!: HTMLCanvasElement;
  let container!: HTMLDivElement;

  let gl: WebGL2RenderingContext | null = null;
  let prog: WebGLProgram | null = null;
  let volTex: WebGLTexture | null = null;
  let lutTex: WebGLTexture | null = null;
  let lastUploadedVolume: Volume | null = null;

  const [cam, setCam] = createSignal<CameraState>(defaultCamera(), { equals: false });
  const [fps, setFps] = createSignal(0);

  // Quality scales the ray-march step count. 1.0 = crisp, 0.5 = draft.
  // Drops during active interaction so drags feel snappy on CPUs w/
  // integrated GPUs; snaps back on pointer up for the final still.
  const [quality, setQuality] = createSignal(1.0);

  function uploadVolume(v: Volume) {
    if (!gl) return;
    if (volTex) gl.deleteTexture(volTex);
    volTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_3D, volTex);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);
    // R16UI is the only integer-monochrome 3-D format guaranteed by
    // WebGL2. usampler3D in the shader reads the raw 16-bit value.
    if (v.data instanceof Uint16Array) {
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 2);
      gl.texImage3D(
        gl.TEXTURE_3D, 0, gl.R16UI,
        v.width, v.height, v.depth,
        0, gl.RED_INTEGER, gl.UNSIGNED_SHORT,
        v.data,
      );
    } else {
      // 8-bit fallback — widen to Uint16 for the same shader path
      const widened = new Uint16Array(v.data.length);
      widened.set(v.data);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 2);
      gl.texImage3D(
        gl.TEXTURE_3D, 0, gl.R16UI,
        v.width, v.height, v.depth,
        0, gl.RED_INTEGER, gl.UNSIGNED_SHORT,
        widened,
      );
    }
    lastUploadedVolume = v;
  }

  function uploadLUT(preset?: VolumePreset) {
    if (!gl) return;
    if (!lutTex) {
      lutTex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, lutTex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }
    const lut = buildColormapLUT(preset?.colormap);
    gl.bindTexture(gl.TEXTURE_2D, lutTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA,
      256, 1, 0,
      gl.RGBA, gl.UNSIGNED_BYTE, lut,
    );
  }

  // ---------- Draw loop ----------
  function resize() {
    if (!gl || !canvas || !container) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(container.clientWidth  * dpr));
    const h = Math.max(1, Math.floor(container.clientHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w; canvas.height = h;
      gl.viewport(0, 0, w, h);
    }
  }

  let frameId = 0;
  let lastFrameTs = 0;
  function draw() {
    if (!gl || !prog || !volTex || !lastUploadedVolume) return;
    resize();
    const v = lastUploadedVolume;
    const aspect = canvas.width / Math.max(1, canvas.height);
    const view = viewMatrix(cam());
    const proj = perspective(cam().fovY, aspect, 0.01, 100);
    const vp = multiply(proj, view);
    const invVP = invertM4(vp);
    const camPos = position(cam());
    const modeInt = props.mode === "mip" ? 0 : props.mode === "minip" ? 2 : 1;

    gl.useProgram(prog);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_3D, volTex);
    gl.uniform1i(gl.getUniformLocation(prog, "u_vol"), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, lutTex);
    gl.uniform1i(gl.getUniformLocation(prog, "u_lut"), 1);

    gl.uniformMatrix4fv(gl.getUniformLocation(prog, "u_invVP"), false, invVP);
    gl.uniform3f(gl.getUniformLocation(prog, "u_camPos"), camPos[0], camPos[1], camPos[2]);
    gl.uniform3f(gl.getUniformLocation(prog, "u_volScale"), 1, 1, 1);
    gl.uniform3i(gl.getUniformLocation(prog, "u_volDims"), v.width, v.height, v.depth);
    gl.uniform1f(gl.getUniformLocation(prog, "u_slope"),     v.slope);
    gl.uniform1f(gl.getUniformLocation(prog, "u_intercept"), v.intercept);
    gl.uniform1f(gl.getUniformLocation(prog, "u_wc"), props.wc);
    gl.uniform1f(gl.getUniformLocation(prog, "u_ww"), Math.max(1, props.ww));
    gl.uniform1f(gl.getUniformLocation(prog, "u_signedBias"), v.signed ? 32768 : 0);
    gl.uniform1i(gl.getUniformLocation(prog, "u_mode"), modeInt);
    gl.uniform1f(gl.getUniformLocation(prog, "u_quality"), quality());
    gl.uniform1f(gl.getUniformLocation(prog, "u_invert"),
                 v.photometric === "MONOCHROME1" ? 1 : 0);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    const now = performance.now();
    if (lastFrameTs > 0) {
      const dt = now - lastFrameTs;
      if (dt > 0) setFps(1000 / dt);
    }
    lastFrameTs = now;
  }

  function redraw() {
    cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(draw);
  }

  onMount(() => {
    gl = canvas.getContext("webgl2", { preserveDrawingBuffer: false, antialias: true });
    if (!gl) throw new Error("WebGL2 not supported");
    prog = program(gl, VS, FS);

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    uploadVolume(props.volume);
    uploadLUT(props.preset);
    redraw();

    const ro = new ResizeObserver(() => redraw());
    ro.observe(container);
    onCleanup(() => ro.disconnect());
  });

  // Redraw whenever any reactive input changes.
  createEffect(() => {
    props.volume;                       // track
    if (!gl) return;
    if (lastUploadedVolume !== props.volume) uploadVolume(props.volume);
    redraw();
  });
  createEffect(() => { props.preset; if (gl) { uploadLUT(props.preset); redraw(); } });
  createEffect(() => { props.wc; props.ww; props.mode; cam(); quality(); redraw(); });

  // ---------- Pointer gestures ----------
  // Button / modifier → gesture:
  //   primary (no Alt)              = rotate
  //   primary + Alt / middle button = pan
  //   right-button (Shift+primary)  = window / level   (matches 2-D)
  // All three dim render quality to 0.5× while dragging.
  type GestureKind = "rotate" | "pan" | "wl";
  let gesture: null | { kind: GestureKind; x: number; y: number } = null;

  function onDown(e: PointerEvent) {
    canvas.setPointerCapture(e.pointerId);
    let kind: GestureKind;
    if (e.button === 2 || (e.button === 0 && e.shiftKey)) {
      kind = "wl";
    } else if (e.button === 1 || e.altKey) {
      kind = "pan";
    } else {
      kind = "rotate";
    }
    gesture = { kind, x: e.clientX, y: e.clientY };
    setQuality(0.5);
    e.preventDefault();
  }
  function onMove(e: PointerEvent) {
    if (!gesture) return;
    const dx = e.clientX - gesture.x;
    const dy = e.clientY - gesture.y;
    gesture.x = e.clientX;
    gesture.y = e.clientY;
    if (gesture.kind === "rotate") {
      const c = { ...cam(), target: [...cam().target] as [number, number, number] };
      camRotate(c, dx, dy, canvas.clientHeight);
      setCam(c);
      props.onCameraChange?.(c);
    } else if (gesture.kind === "pan") {
      const c = { ...cam(), target: [...cam().target] as [number, number, number] };
      camPan(c, dx, dy, canvas.clientHeight);
      setCam(c);
      props.onCameraChange?.(c);
    } else {
      // W/L — horizontal drag widens WW, vertical shifts WC. Scale
      // relative to the current band so narrow windows (brain ±80)
      // don't leap when the user tugs one pixel.
      const ww = Math.max(1, props.ww + dx * Math.max(1, props.ww) * 0.004);
      const wc = props.wc - dy * Math.max(1, props.ww) * 0.004;
      props.onWindowChange?.(wc, ww);
    }
  }
  function onUp(e: PointerEvent) {
    gesture = null;
    setQuality(1.0);
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* fine */ }
    redraw();
  }
  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const c = { ...cam(), target: [...cam().target] as [number, number, number] };
    camDolly(c, e.deltaY / 120);
    setCam(c);
  }
  function onDbl() {
    const c = { ...cam(), target: [...cam().target] as [number, number, number] };
    camReset(c);
    setCam(c);
  }

  return (
    <div ref={container} class="relative flex h-full w-full items-center justify-center bg-ink-0 touch-none">
      <canvas ref={canvas}
              class="absolute inset-0 h-full w-full"
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onWheel={onWheel}
              onDblClick={onDbl}
              onContextMenu={(e) => e.preventDefault()} />
      <div class="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80 leading-4">
        3D · {props.mode.toUpperCase()} · {Math.round(fps())} fps
        {" · "}az <span class="text-brand">{(cam().azimuth * 180 / Math.PI).toFixed(0)}°</span>
        {" "}el <span class="text-brand">{(cam().elevation * 180 / Math.PI).toFixed(0)}°</span>
        {" "}d <span class="text-brand">{cam().distance.toFixed(2)}</span>
      </div>
      <div class="pointer-events-none absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 font-mono text-[10px] text-white/80 leading-4">
        drag = rotate · wheel = zoom · alt-drag = pan · right-drag = W/L · dbl-click = reset
      </div>
    </div>
  );
}
